import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import duration from 'dayjs/plugin/duration';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isBetween from 'dayjs/plugin/isBetween';
import localizedFormat from 'dayjs/plugin/localizedFormat';

import 'dayjs/locale/es';

dayjs.extend(utc);
dayjs.extend(duration);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);
dayjs.extend(localizedFormat);

const DATE_FORMATS = {
  DEFAULT: 'DD/MM/YYYY',
  NAME_MONTH: 'DD MMMM YYYY',
  MONTH_ABBREVIATION: 'D MMM YYYY',
  ISO: 'YYYY-MM-DDTHH:mm:ss',
  TIME: 'HH:mm',
  YYYY_MM_DD: 'YYYY-MM-DD',
  DESC: 'Do MMM, YYYY',
  NAME_MONTH_ONLY: 'MMMM',
  YEAR_ONLY: 'YYYY',
  ABBREVIATION_MONTH_ONLY: 'MMM',
  NUMBER_DAY_ONLY: 'DD',
  TIME_HOUR: 'HH:mm [h]',
  TIME_HOURS_MINUTES: 'H [h] mm [min]',
  TIME_MINUTES: 'mm [min]',
  DAY_MONTH: 'DD MMMM',
  MONTH_YEAR: 'MMM YYYY',
  UNIX_TIMESTAMP: 'X',
  FULL_DESC: 'dddd, D [de] MMMM [de] YYYY'
};

dayjs.locale('es');

const isSameInstance = (date, controlDate = dayjs(), valueToCheck = 'day') => {
  const controlDateInstance = dayjs.isDayjs(controlDate) ? controlDate : dayjs(controlDate);
  return controlDateInstance.isSame(dayjs(date), valueToCheck);
};

export const dayjsConvert = date => dayjs(date).tz('Europe/Madrid');

export const updateLocale = (lang, config) => dayjs.locale(lang, config);

export const dateToday = () => dayjs().tz('Europe/Madrid');

export const isAfter = date => dayjs().isAfter(date);

export const dateTodayUnixTimestamp = () =>
  dayjs().tz('Europe/Madrid').format(DATE_FORMATS.UNIX_TIMESTAMP);

export const dateToString = (val, format = DATE_FORMATS.DEFAULT) =>
  val ? dayjs(val).tz('Europe/Madrid').format(format) : '';

export const dateToStringWithNameMonth = val => dateToString(val, DATE_FORMATS.NAME_MONTH);

export const dateWithNameMonthOnly = val => dateToString(val, DATE_FORMATS.NAME_MONTH_ONLY);

export const dateWithYearOnly = val => dateToString(val, DATE_FORMATS.YEAR_ONLY);

export const dateWithAbbreviationMonthOnly = val =>
  dateToString(val, DATE_FORMATS.ABBREVIATION_MONTH_ONLY).replace('.', '');

export const dateMonthAbbreviation = val => dateToString(val, DATE_FORMATS.MONTH_ABBREVIATION);

export const dateWithDayOnly = val => dateToString(val, DATE_FORMATS.NUMBER_DAY_ONLY);

export const dateWithDayAndMonth = val => dateToString(val, DATE_FORMATS.DAY_MONTH);

export const dateWithMonthAndYear = val =>
  dateToString(val, DATE_FORMATS.MONTH_YEAR).replace('.', '');

export const timeWithHoursAndMinutes = val => dateToString(val, DATE_FORMATS.TIME);

export const timeWithHours = val => dayjs(val, 'HH:mm:ss').format(DATE_FORMATS.TIME_HOUR);

export const timeWithMinutes = val =>
  dayjs
    .utc()
    .startOf('day')
    .add(val, 'minutes')
    .format(val >= 60 ? DATE_FORMATS.TIME_HOURS_MINUTES : DATE_FORMATS.TIME_MINUTES);

export const dateToStringDescription = val => dateToString(val, DATE_FORMATS.DESC);

export const stringToDate = (val, format = DATE_FORMATS.DEFAULT) =>
  dayjs.tz(val, format, 'Europe/Madrid');

export const dateToISO = (val, format = DATE_FORMATS.DEFAULT) =>
  val ? dayjs.tz(val, format, 'Europe/Madrid').format(DATE_FORMATS.ISO) : '';

export const isoToDate = (val, format = DATE_FORMATS.ISO) =>
  val ? dayjs.tz(val, format, 'Europe/Madrid').format(DATE_FORMATS.DEFAULT) : '';

export const changeFormatFromYearFirst = val =>
  dayjs.tz(val, DATE_FORMATS.YYYY_MM_DD).format(DATE_FORMATS.DEFAULT);

export const isValidDate = (val, format = DATE_FORMATS.DEFAULT) => dayjs(val, format).isValid();

export const dateToCalendarPicker = val => dateToString(val, DATE_FORMATS.YYYY_MM_DD);

export const dateToFullDesc = val => dateToString(val, DATE_FORMATS.FULL_DESC);

export const dateNormal = val => dateToString(val, DATE_FORMATS.DEFAULT);

export const dateJoinWithTime = (date, time) => {
  const dateObject = dayjs(date);
  const timeObject = dayjs(time, 'HH:mm:ss');
  const combinedDateTime = dateObject
    .set('hour', timeObject.hour())
    .set('minute', timeObject.minute())
    .set('second', timeObject.second());
  return combinedDateTime.tz('Europe/Madrid').format(DATE_FORMATS.ISO);
};

export const dateIsEarlier = (date, value) => {
  const formatedDate = new Date(date);
  const formatedSecondDate = new Date(value);
  if (isNaN(formatedDate.getTime()) || isNaN(formatedSecondDate.getTime())) {
    return false;
  }
  return formatedDate >= formatedSecondDate;
};

export const differenceBetweenDates = val => {
  const now = dayjs();
  const expiration = dayjs(val);
  const diffDuration = dayjs.duration(expiration.diff(now));
  return diffDuration.months() > 0
    ? `${diffDuration.months()} m ${diffDuration.days()} d`
    : diffDuration.days() === 0
      ? `${diffDuration.hours()} h ${diffDuration.minutes()} min`
      : `${diffDuration.days()} d ${diffDuration.hours()} h`;
};

export const differenceBetweenDatesWithText = val => {
  const now = dayjs();
  const notification = dayjs(val).local();
  const diffDuration = dayjs.duration(now.diff(notification));
  return diffDuration.months() > 0
    ? getTimeText(diffDuration.months(), 'month')
    : Math.abs(diffDuration.weeks()) > 0
      ? getTimeText(diffDuration.weeks(), 'week')
      : Math.abs(diffDuration.days()) > 0
        ? getTimeText(diffDuration.days(), 'day')
        : Math.abs(diffDuration.hours()) > 0
          ? getTimeText(diffDuration.hours(), 'hour')
          : Math.abs(diffDuration.minutes()) > 0
            ? getTimeText(diffDuration.minutes(), 'minute')
            : 'menos de un minuto';
};

export const isLessThanOneDay = val => dayjs().diff(dayjs(val), 'days') <= 0;

export const isLessThan = (date, type = 'days', time = 0) => {
  const now = dayjs().format(DATE_FORMATS.ISO);
  const notification = dayjs(date).utc().format(DATE_FORMATS.ISO);
  return dayjs(now).diff(notification, type) <= time;
};

const getTimeText = (value, type) => {
  const timeTexts = {
    month: ['mes', 'meses'],
    week: ['semana', 'semanas'],
    day: ['día', 'días'],
    hour: ['hora', 'horas'],
    minute: ['minuto', 'minutos']
  };
  return `${Math.abs(value)} ${timeTexts[type][Math.abs(value) > 1 ? 1 : 0]}`;
};

export const countDown = deadline => {
  const expiration = dayjs(deadline);
  if (isAfter(expiration)) return '';
  const diff = expiration.diff(dayjs());
  return `${Math.floor(diff / (1000 * 60 * 60 * 24))} d ${Math.floor(
    (diff / (1000 * 60 * 60)) % 24
  )} h`;
};

export const addTime = (date, time = 0, type) => dayjs(date).add(time, type);

export const setHour = (date, hour) => dayjs(`${date} ${hour}`, DATE_FORMATS.ISO);

export const isSameDate = (date, controlDate = dayjs()) => isSameInstance(date, controlDate);

export const isSameMonth = (date, controlDate = dayjs()) =>
  isSameInstance(date, controlDate, 'month');

export const isSameOrBeforeDate = (date, controlDate = dayjs()) => {
  const controlDateInstance = dayjs.isDayjs(controlDate) ? controlDate : dayjs(controlDate);
  return controlDateInstance.isSameOrBefore(dayjs(date), 'day');
};

export const isSameOrAfterDate = (date, controlDate = dayjs()) => {
  const controlDateInstance = dayjs.isDayjs(controlDate) ? controlDate : dayjs(controlDate);
  return controlDateInstance.isSameOrBefore(date, 'day') || isSameDate(date, controlDate);
};

export const getTimestamp = () => Math.floor(Date.now() / 1000);

export const getTimestampYesterday = () =>
  Math.floor(dayjs().subtract(1, 'day').startOf('day').valueOf() / 1000);

export const getDateYesterday = () =>
  Math.floor(dayjs().subtract(1, 'day').set('hour', 23).set('minute', 0).valueOf() / 1000);

export const isBetweenDates = (startDate, endDate) => {
  const dateInstance = dateToday();
  const startDateInstance = dayjsConvert(startDate);
  const endDateInstance = dayjsConvert(endDate);
  return dateInstance.isBetween(startDateInstance, endDateInstance, null, '[]');
};
